import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C } from "@coremonit/design-system";
import { Search, ArrowRight } from "lucide-react";
import { MICROSERVICES_NAV } from "./nav";
import { useProjects, useGlobalSearch } from "../api/hooks";

interface Props { open: boolean; onClose: () => void }

export function CommandPalette({ open, onClose }: Props) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchEnabled = q.trim().length >= 2;

  useEffect(() => {
    if (!open) { setQ(""); setIdx(0); return; }
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const projects = useProjects();
  const globalSearch = useGlobalSearch(q, open && searchEnabled);

  const items = useMemo(() => {
    if (searchEnabled && globalSearch.data?.items.length) {
      return globalSearch.data.items.map((r) => ({
        id: r.id,
        label: r.title,
        subtitle: r.subtitle,
        path: r.path,
        kind: r.kind,
      }));
    }
    const base: Array<{ id: string; label: string; subtitle?: string; path: string; kind: string }> = [
      ...MICROSERVICES_NAV.map((n) => ({ id: n.id, label: n.label, path: n.path, kind: "page" })),
      ...(projects.data?.items ?? []).map((p) => ({ id: p.id, label: `Project · ${p.name}`, path: `/projects/${p.key}`, kind: "project" })),
      { id: "workspace", label: "Operations Workspace", path: "/workspace", kind: "page" },
      { id: "cluster-prod", label: "Cluster · prod-eks-01", path: "/clusters/prod-eks-01", kind: "cluster" },
    ];
    if (!q.trim()) return base;
    const s = q.toLowerCase();
    return base.filter((n) => n.label.toLowerCase().includes(s));
  }, [q, projects.data, searchEnabled, globalSearch.data]);

  useEffect(() => {
    setIdx(0);
  }, [items.length, q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, items.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
      if (e.key === "Enter" && items[idx]) { onClose(); nav(items[idx].path); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, idx, nav, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 40,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: "12vh",
        background: "rgba(7,13,26,0.7)", backdropFilter: "blur(10px)",
      }}
    >
      <div style={{ width: "100%", maxWidth: 560, background: C.surface, border: `1px solid ${C.borderBright}`, borderRadius: 18, boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(45,127,249,0.15)`, overflow: "hidden" }}>
        <div style={{ padding: 10, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: C.surfaceHigh, borderRadius: 12 }}>
            <Search size={13} color={C.textMuted} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => { setQ(e.target.value); setIdx(0); }}
              placeholder="Search clusters, pods, services, traces…"
              style={{ background: "transparent", border: "none", outline: "none", color: C.text, fontSize: 13, flex: 1 }}
            />
            <span style={{ fontSize: 9, color: C.textMuted, padding: "2px 5px", background: C.surface, borderRadius: 5, border: `1px solid ${C.border}` }}>Esc</span>
          </div>
        </div>
        <div style={{ maxHeight: 380, overflowY: "auto", padding: 8 }}>
          {globalSearch.isFetching && searchEnabled && (
            <div style={{ padding: 12, color: C.textMuted, fontSize: 11 }}>Searching infrastructure…</div>
          )}
          {items.length === 0 && !globalSearch.isFetching && (
            <div style={{ padding: 20, color: C.textMuted, textAlign: "center", fontSize: 12 }}>No matches</div>
          )}
          {items.map((it, i) => (
            <button
              key={`${it.kind}_${it.id}`}
              onMouseEnter={() => setIdx(i)}
              onClick={() => { onClose(); nav(it.path); }}
              style={{
                width: "100%", textAlign: "left",
                display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8,
                background: i === idx ? C.blueDim : "transparent",
                border: `1px solid ${i === idx ? C.borderBright : "transparent"}`,
                color: i === idx ? C.text : C.textSub,
                cursor: "pointer", fontSize: 12,
              }}
            >
              <span style={{ flex: 1 }}>
                {it.label}
                {it.subtitle && <span style={{ display: "block", color: C.textMuted, fontSize: 10, marginTop: 2 }}>{it.subtitle}</span>}
              </span>
              <span style={{ fontSize: 9, color: C.textMuted, textTransform: "uppercase" }}>{it.kind}</span>
              <ArrowRight size={12} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
